import { DataLayer, FeatureInspectorConfig, LayerControlConfig } from "./types";

// Facade material color mapping
export const FACADE_MATERIAL_COLORS: Record<string, string> = {
  Bricks: "#B7410E",
  Concrete: "#888888",
  Unknown: "#CCCCCC",
  Vinyl: "#F5F5DC",
  Glass: "#87CEEB",
  Metal: "#708090",
  Stone: "#696969",
  Wood: "#8B4513",
};

// Roof condition color mapping (green to red by severity)
export const ROOF_CONDITION_COLORS: Record<string, string> = {
  excellent: "#22C55E",
  good: "#84CC16",
  fair: "#EAB308",
  poor: "#F97316",
  bad: "#EF4444",
  damaged: "#DC2626",
};

// Building age color mapping (light purple for older, dark purple for newer)
export const BUILDING_AGE_COLORS: Record<string, string> = {
  "Before 1950": "#E6D5F5", // Very light purple (oldest)
  "1950-1979": "#C9B3E0", // Light purple
  "1980-1990": "#A78BC9", // Medium light purple
  "1991-2003": "#8B6BB3", // Medium purple
  "2004-2016": "#6C4A9C", // Medium dark purple
  "2017-2025": "#4A2A75", // Dark purple (newest)
  Unknown: "#A9A9A9", // Grey for unknown/no data
};

// First floor elevation color mapping
export const FIRST_FLOOR_ELEVATION_COLORS: Record<string, string> = {
  "0.0-0.5m": "#704C99",
  "0.5-1.0m": "#916FB8",
  "1.0m+": "#B198CD",
  "No FFE": "#A9A9A9", // Grey for no data
};

// Planning zone color mapping by category
export const PLANNING_ZONE_COLORS: Record<string, string> = {
  // Residential
  R1: "#FFFFBE", // General Residential
  R2: "#FFFF00", // Low Density Residential
  R3: "#FFAA00", // Medium Density Residential
  R4: "#FF7F00", // High Density Residential
  R5: "#FFFFE5", // Large Lot Residential

  // Conservation
  C1: "#007F00", // National Parks & Reserves
  C2: "#55AA00", // Environmental Conservation
  C3: "#AAFF00", // Environmental Management
  C4: "#D3FFBE", // Environmental Living

  // Rural
  RU1: "#B3B35A", // Primary Production
  RU2: "#E8E8B3", // Rural Landscape
  RU3: "#8C8C46", // Forestry
  RU4: "#996633", // Primary Production Small Lots
  RU5: "#FFBEE8", // Village

  // Special Purpose
  SP1: "#FF0000", // Special Activities
  SP2: "#C80000", // Infrastructure
  SP3: "#FF5500", // Tourist

  // Recreation
  RE1: "#00FF00", // Public Recreation
  RE2: "#00AA00", // Private Recreation

  // Employment
  E1: "#00AAFF", // Local Centre
  E2: "#0055FF", // Commercial Centre
  E3: "#E8BEFF", // Productivity Support
  E4: "#AA00FF", // General Industrial
  E5: "#7F00FF", // Heavy Industrial

  // Mixed Use
  MU1: "#1764FF", // Mixed Use

  // Waterway
  W1: "#AAE5EE",
  W2: "#AAE5EE",
  W3: "#AAE5EE",
  W4: "#AAE5EE",
  W5: "#AAE5EE",
};

// Planning zone descriptions for legend
export const PLANNING_ZONE_LEGEND: Array<{
  category: string;
  zones: Array<{ code: string; name: string; color: string }>;
}> = [
  {
    category: "Residential",
    zones: [
      { code: "R1", name: "General Residential", color: "#FFFFBE" },
      { code: "R2", name: "Low Density Residential", color: "#FFFF00" },
      { code: "R3", name: "Medium Density Residential", color: "#FFAA00" },
      { code: "R4", name: "High Density Residential", color: "#FF7F00" },
      { code: "R5", name: "Large Lot Residential", color: "#FFFFE5" },
    ],
  },
  {
    category: "Conservation",
    zones: [
      { code: "C1", name: "National Parks & Reserves", color: "#007F00" },
      { code: "C2", name: "Environmental Conservation", color: "#55AA00" },
      { code: "C3", name: "Environmental Management", color: "#AAFF00" },
      { code: "C4", name: "Environmental Living", color: "#D3FFBE" },
    ],
  },
  {
    category: "Rural",
    zones: [
      { code: "RU1", name: "Primary Production", color: "#B3B35A" },
      { code: "RU2", name: "Rural Landscape", color: "#E8E8B3" },
      { code: "RU3", name: "Forestry", color: "#8C8C46" },
      { code: "RU4", name: "Primary Production Small Lots", color: "#996633" },
      { code: "RU5", name: "Village", color: "#FFBEE8" },
    ],
  },
  {
    category: "Special Purpose",
    zones: [
      { code: "SP1", name: "Special Activities", color: "#FF0000" },
      { code: "SP2", name: "Infrastructure", color: "#C80000" },
      { code: "SP3", name: "Tourist", color: "#FF5500" },
    ],
  },
  {
    category: "Recreation",
    zones: [
      { code: "RE1", name: "Public Recreation", color: "#00FF00" },
      { code: "RE2", name: "Private Recreation", color: "#00AA00" },
    ],
  },
  {
    category: "Employment",
    zones: [
      { code: "E1", name: "Local Centre", color: "#00AAFF" },
      { code: "E2", name: "Commercial Centre", color: "#0055FF" },
      { code: "E3", name: "Productivity Support", color: "#E8BEFF" },
      { code: "E4", name: "General Industrial", color: "#AA00FF" },
      { code: "E5", name: "Heavy Industrial", color: "#7F00FF" },
    ],
  },
  {
    category: "Mixed Use",
    zones: [{ code: "MU1", name: "Mixed Use", color: "#1764FF" }],
  },
  {
    category: "Waterway",
    zones: [{ code: "W1, W2, W3, etc.", name: "Waterways", color: "#AAE5EE" }],
  },
];

export const LAYER_CONFIG: LayerControlConfig[] = [
  {
    id: "gnaf",
    label: "G-NAF",
    enabled: false,
    type: "checkbox",
    mapLayers: ["address"],
  },
  {
    id: "national-solar",
    label: "National Solar",
    enabled: true,
    type: "checkbox",
    mapLayers: ["solar_heights"],
  },
  {
    id: "national-buildings",
    label: "National Buildings",
    enabled: true,
    type: "checkbox",
    mapLayers: ["building_parts"],
    radioOptions: [
      { label: "Default", value: "default", selected: true },
      { label: "Facade Material (Insight Pack)", value: "facade" },
      { label: "First Floor Elevation (Insight Pack)", value: "elevation" },
      { label: "Roof (Insight Pack)", value: "roof" },
      { label: "Roof Condition (Insight Pack)", value: "roof-condition" },
      { label: "Solar (Insight Pack)", value: "solar" },
      { label: "Age (Insight Pack)", value: "age" },
    ],
  },
  {
    id: "national-trees",
    label: "National Trees",
    enabled: true,
    type: "checkbox",
    mapLayers: ["trees"],
  },
  {
    id: "national-roads",
    label: "National Roads",
    enabled: true,
    type: "checkbox",
    mapLayers: ["roads"],
    radioOptions: [
      { label: "Default", value: "default", selected: true },
      { label: "Labels", value: "labels" },
      { label: "Speeds", value: "speeds" },
      // { label: "Restrictions", value: "restrictions" },
    ],
  },
  {
    id: "national-cadastre",
    label: "National Cadastre",
    enabled: true,
    type: "checkbox",
    mapLayers: ["cadastre"],
    radioOptions: [
      { label: "Default", value: "default", selected: true },
      { label: "Planning (Insight Pack)", value: "planning" },
    ],
  },
  {
    id: "national-property",
    label: "National Property",
    enabled: false,
    type: "checkbox",
    mapLayers: ["property"],
    // radioOptions: [
    //   { label: "Default", value: "default", selected: true },
    //   // { label: "Planning (Insight Pack)", value: "planning" },
    // ],
  },
  {
    id: "roof-condition-2025",
    label: "Roof Condition 2025",
    enabled: false,
    type: "checkbox",
    mapLayers: [
      "roof_condition_2025_discoloration",
      "roof_condition_2025_ponding",
      "roof_condition_2025_rust",
      "roof_condition_2025_tarps",
    ],
    radioOptions: [
      { label: "Discoloration", value: "discoloration", selected: true },
      { label: "Ponding", value: "ponding" },
      { label: "Rust", value: "rust" },
      { label: "Tarps", value: "tarps" },
    ],
  },
  {
    id: "national-surface-cover",
    label: "National Surface Cover (2m)",
    enabled: true,
    type: "checkbox",
    mapLayers: ["surface-cover"],
  },
  // {
  //   id: "comparison-slider",
  //   label: "Comparison Slider",
  //   enabled: false,
  //   type: "switch",
  // },
  {
    id: "basemap",
    label: "Basemap",
    value: "None",
    type: "selector",
    options: ["None", "Geoscape", "Satellite", "Aerial Aerometrex 2025"],
  },
  // {
  //   id: "time-of-day",
  //   label: "Time of Day",
  //   value: "Day",
  //   type: "selector",
  //   options: ["Dawn", "Day", "Dusk", "Night"],
  // },

  {
    id: "product-documentation",
    label: "Product Documentation",
    type: "links",
    links: [
      {
        label: "National Buildings Guide",
        href: "https://docs.geoscape.com.au/projects/buildings_guide/en/4.0/",
      },
      {
        label: "Building Height - Insight Pack",
        href: "https://docs.geoscape.com.au/projects/building_height_insight_pack/en/stable/index.html",
      },
      {
        label: "Building Roof - Insight Pack",
        href: "https://docs.geoscape.com.au/projects/building_roof_insight_pack/en/stable/index.html",
      },
      {
        label: "Building Solar - Insight Pack",
        href: "https://docs.geoscape.com.au/projects/building_solar_insight_pack/en/stable/index.html",
      },
      {
        label: "Address Pool - Insight Pack",
        href: "https://docs.geoscape.com.au/projects/address_pool_insight_pack/en/stable/index.html",
      },
    ],
  },
  {
    id: "download-sample",
    label: "Download Sample",
    type: "button",
  },
];

const BASE_DATA_LAYERS: Omit<DataLayer, "enabled">[] = [
  {
    name: "surface-cover",
    label: "National Surface Cover (2m)",
    type: "raster",
    color: "#8B4513",
  },
  {
    name: "roof_condition_2025_discoloration",
    label: "Roof Condition 2025 - Discoloration",
    type: "fill",
    color: "#FF006E",
    sourceLayer: "chatswood_roof_condition_2025_discoloration",
    folder: "roof_condition_2025_discoloration_tiles",
  },
  {
    name: "roof_condition_2025_ponding",
    label: "Roof Condition 2025 - Ponding",
    type: "fill",
    color: "#8338EC",
    sourceLayer: "chatswood_roof_condition_2025_ponding",
    folder: "roof_condition_2025_ponding_tiles",
  },
  {
    name: "roof_condition_2025_rust",
    label: "Roof Condition 2025 - Rust",
    type: "fill",
    color: "#FB5607",
    sourceLayer: "chatswood_roof_condition_2025_rust",
    folder: "roof_condition_2025_rust_tiles",
  },
  {
    name: "roof_condition_2025_tarps",
    label: "Roof Condition 2025 - Tarps",
    type: "fill",
    color: "#FFBE0B",
    sourceLayer: "chatswood_roof_condition_2025_tarps",
    folder: "roof_condition_2025_tarps_tiles",
  },
  {
    name: "property",
    label: "National Property",
    type: "line",
    color: "#853A21",
    sourceLayer: "chatswood_property",
    folder: "property_tiles",
  },
  {
    name: "cadastre",
    label: "National Cadastre",
    type: "line",
    color: "#553973",
    sourceLayer: "chatswood_cadastre",
    folder: "cadastre_tiles",
  },
  {
    name: "roads",
    label: "National Roads",
    type: "line",
    color: "#444455",
    sourceLayer: "chatswood_roads",
    folder: "roads_tiles",
  },
  {
    name: "trees",
    label: "National Trees",
    type: "extrude",
    color: "#96ceb4",
    sourceLayer: "chatswood_trees",
    folder: "trees_tiles",
  },
  {
    name: "building",
    label: "Buildings (No HD)",
    type: "extrude",
    color: "#A9A9A9",
    sourceLayer: "chatswood_buildings",
    folder: "buildings_tiles",
  },
  {
    name: "building_parts",
    label: "Buildings",
    type: "extrude",
    color: "#A9A9A9",
    sourceLayer: "chatswood_buildings",
    folder: "buildings_tiles",
  },
  {
    name: "solar",
    label: "Solar Panels (No Height)",
    type: "fill",
    color: "#EDC228",
    sourceLayer: "chatswood_solar",
    folder: "solar_tiles",
  },
  {
    name: "solar_heights",
    label: "Solar Panels",
    type: "extrude",
    color: "#EDC228",
    sourceLayer: "solar_panels",
    folder: "solar_heights_tiles",
  },
  {
    name: "address",
    label: "Addresses",
    type: "circle",
    color: "#E4002B",
    sourceLayer: "chatswood_address",
    folder: "address_tiles",
  },
];

// Helper function to get enabled state from LAYER_CONFIG
const getLayerEnabledState = (layerName: string): boolean => {
  const controlConfig = LAYER_CONFIG.find((config) =>
    config.mapLayers?.includes(layerName),
  );

  if (controlConfig) {
    return controlConfig.enabled ?? false;
  }

  // Default enabled state for layers not controlled by LAYER_CONFIG
  const defaultEnabled: Record<string, boolean> = {
    building: false,
    solar: false,
  };

  return defaultEnabled[layerName] ?? true;
};

// Generate DATA_LAYERS with enabled state derived from LAYER_CONFIG
export const DATA_LAYERS: DataLayer[] = BASE_DATA_LAYERS.map((layer) => ({
  ...layer,
  enabled: getLayerEnabledState(layer.name),
}));

// Feature Inspector Table Configuration - organized by data layer type
export const FEATURE_INSPECTOR_CONFIG: FeatureInspectorConfig = {
  // Buildings feature inspector configuration
  building: {
    tables: [
      {
        key: "building",
        displayName: "National Buildings",
        attributes: [
          "building_pid",
          "date_created",
          "date_modified",
          "date_first_captured",
          "date_last_captured",
          "capture_resolution",
          "capture_method",
          "locality_pid",
          "locality_name",
          "lga_pid",
          "lga_name",
          "mesh_block_code",
          "sa1_code",
          "state",
          "building_use",
          "building_use_source",
          "address_count",
          "primary_building_flag",
          "ground_elevation",
          "ground_elevation_resolution",
          "building_area",
          "total_floor_area",
          "number_vertices",
          "centroid_longitude",
          "centroid_latitude",
        ],
      },
      {
        key: "building_height",
        displayName: "Height Insight Pack",
        attributes: [
          "building_pid",
          "date_created",
          "date_modified",
          "roof_height",
          "eave_height",
          "building_volume",
          "estimated_floors",
          "state",
        ],
      },
      {
        key: "building_roof",
        displayName: "Roof Insight Pack",
        attributes: [
          "building_pid",
          "date_created",
          "date_modified",
          "roof_colour",
          "roof_shape",
          "primary_roof_material",
          "roof_slope",
          "tree_overhang",
          "state",
        ],
      },
      {
        key: "building_solar",
        displayName: "Solar Insight Pack",
        attributes: [
          "building_pid",
          "date_created",
          "date_modified",
          "date_last_captured",
          "capture_resolution",
          "capture_method",
          "solar_flag",
          "solar_area",
          "daily_estimated_power",
          "state",
        ],
      },
      {
        key: "building_age",
        displayName: "Age Insight Pack",
        attributes: [
          "building_pid",
          "date_created",
          "date_modified",
          "built_start_year",
          "built_end_year",
          "capture_method",
          "state",
        ],
      },
      {
        key: "building_facade_material",
        displayName: "Facade Material Insight Pack",
        attributes: [
          "building_pid",
          "date_created",
          "date_modified",
          "date_first_captured",
          "date_last_captured",
          "capture_method",
          "facade_material",
          "state",
        ],
      },
      {
        key: "building_first_floor_elevation",
        displayName: "First Floor Elevation Insight Pack",
        attributes: [
          "building_pid",
          "date_created",
          "date_modified",
          "date_first_captured",
          "date_last_captured",
          "capture_method",
          "first_floor_elevation",
          "first_floor_elevation_confidence",
          "basement_flag",
          "basement_confidence",
          "garage_flag",
          // "raised_entrance_flag",
          "state",
        ],
      },
      {
        key: "building_roof_condition",
        displayName: "Roof Condition Insight Pack",
        attributes: [
          "building_pid",
          "date_created",
          "date_modified",
          "date_first_captured",
          "date_last_captured",
          "capture_method",
          "roof_condition",
          "discoloration_area",
          "ponding_area",
          "rust_area",
          "tarp_area",
          "state",
        ],
      },
    ],
    excludedTables: ["building_parts"],
  },

  // G-NAF/Address feature inspector configuration
  address: {
    tables: [
      {
        key: "address",
        displayName: "G-NAF",
        attributes: [
          "record_id",
          "date_created",
          "date_modified",
          "internal_address_pid",
          "address_pid",
          "road_number_first",
          "road_name",
          "road_type_short_name",
          "road_type_long_name",
          "locality_name",
          "locality_pid",
          "state",
          "state_pid",
          "postcode",
          "address_label",
          "lga_name",
          "lga_pid",
          "latitude",
          "longitude",
          "geocode_type",
          "gnaf_address_detail_pid",
          "parcel_id",
          "property_id",
          "contributor_id",
          "contributor_address_record_id",
          "contributor_address_pid",
          "address_status",
          "principal_alias",
          "primary_secondary",
          "internal_primary_address_pid",
          "primary_address_pid",
          "mb_code",
          "street_locality_pid",
          "street_locality_latitude",
          "street_locality_longitude",
          "locality_aliases",
          "locality_neighbours",
          "locality_latitude",
          "locality_longitude",
          "record_hash",
          "action",
          "supplier_data_id",
          "record_create_date",
          "record_processed_date",
          "contributor_state",
          "contributor_primary_secondary",
          "geocode_type_long_name",
          "gnaf_contributor_id",
          "gnaf_contributor_pid",
          "contributor_locality_name",
          "contributor_postcode",
          "complex_unit_number",
          "site_name",
          "road_number_last",
          "road_number_first_suffix",
        ],
      },
    ],
    excludedTables: [],
  },

  // Cadastre feature inspector configuration
  cadastre: {
    tables: [
      {
        key: "cadastre",
        displayName: "National Cadastre",
        attributes: [
          "cadastre_polygon_pid",
          "cadastre_pid",
          "contributor_id",
          "date_created",
          "date_modified",
          "state",
          "lot",
          "plan",
          "section",
          "parcel_id",
          "contributor_status",
          "title_status",
          "parcel_type",
          "base_parcel",
          "strata_type",
          "total_strata_count",
          "horizontal_strata_count",
          "vertical_strata_count",
          "area",
        ],
      },
      {
        key: "cadastre",
        displayName: "Planning Insight Pack",
        attributes: [
          "cadastre_polygon_pid",
          "cadastre_pid",
          "date_created",
          "state",
          "zone_code",
          "primary_zone_description",
          "secondary_zone_description",
          "psma_description",
          "source",
          "url",
        ],
      },
    ],
    excludedTables: [],
  },

  // Property feature inspector configuration
  property: {
    tables: [
      {
        key: "property",
        displayName: "National Property",
        attributes: [
          "property_polygon_pid",
          "property_pid",
          "contributor_id",
          "date_created",
          "date_modified",
          "state",
          "source",
          "base_property",
          "area",
        ],
      },
    ],
    excludedTables: [],
  },

  // Roads feature inspector configuration
  roads: {
    tables: [
      {
        key: "roads",
        displayName: "National Roads",
        attributes: [
          "road_pid",
          "contributor_id",
          "date_created",
          "date_modified",
          "national_route",
          "state_route",
          "road_name",
          "road_name_label",
          "road_type",
          "road_type_label",
          "road_suffix",
          "road_suffix_label",
          "road_alias_name",
          "road_alias_type",
          "road_alias_suffix",
          "hierarchy",
          "subtype",
          "lane_count",
          "lane_description",
          "one_way",
          "access_type",
          "status",
          "surface",
          "trafficability",
          "travel_direction",
          "speed",
          "state",
        ],
      },
    ],
    excludedTables: [],
  },

  // Trees feature inspector configuration
  trees: {
    tables: [
      {
        key: "trees",
        displayName: "National Trees",
        attributes: ["id", "height", "state", "zone"],
      },
    ],
    excludedTables: [],
  },

  // Solar feature inspector configuration
  solar: {
    tables: [
      {
        key: "solar_height",
        displayName: "National Solar",
        attributes: [
          "solar_pid",
          "solar_building_pid",
          "date_created",
          "capture_source",
          "capture_date",
          "solar_type",
          "state",
          "solar_area",
          "daily_estimated_power",
        ],
      },
    ],
    excludedTables: [],
  },

  // Surface cover feature inspector configuration
  surfaceCover: {
    tables: [{ key: "surface_cover", displayName: "National Surface Cover" }],
    excludedTables: [],
  },

  // Roof condition 2025 feature inspector configuration
  roofCondition2025: {
    tables: [
      {
        key: "roof_condition_2025_discoloration",
        displayName: "Roof Condition 2025 - Discoloration",
        attributes: ["building_pid", "type", "area"],
      },
      {
        key: "roof_condition_2025_ponding",
        displayName: "Roof Condition 2025 - Ponding",
        attributes: ["building_pid", "type", "area"],
      },
      {
        key: "roof_condition_2025_rust",
        displayName: "Roof Condition 2025 - Rust",
        attributes: ["building_pid", "type", "area"],
      },
      {
        key: "roof_condition_2025_tarps",
        displayName: "Roof Condition 2025 - Tarps",
        attributes: ["building_pid", "type", "area"],
      },
    ],
    excludedTables: [],
  },
};
